import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Package, 
  User, 
  Calendar, 
  MapPin, 
  Truck, 
  CheckCircle, 
  Clock, 
  PoundSterling,
  Search,
  Filter,
  Eye,
  PlusCircle,
  FileDown
} from "lucide-react";
import { useOrders } from "@/hooks/use-orders";
import { useState } from "react";
import { format } from "date-fns";
import { FixedPageHeader } from "@/components/ui/fixed-page-header";
import { PAGE_HEADERS } from "@/constants/page-headers";
import { COMPANY_ADDRESS_LINES, COMPANY_NAME, COMPANY_PDF_FOOTER } from "@/constants/company";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { useUserOrganization } from "@/hooks/use-user-organization";
import { CustomerOrderDialog } from "@/components/crm/CustomerOrderDialog";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import { 
  computeOrderProgress, 
  computeOrderStages, 
  getOrderStatusColor, 
  getProgressBarColor 
} from "@/utils/orderProgress";
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { computeOrderFinancials } from "@/lib/orderPdfCalc";
import { useToast } from "@/hooks/use-toast";
import { DeliveryAddressBlock } from "@/components/crm/DeliveryAddressBlock";

export default function AdminOrders() {
  const { data: orders = [], isLoading } = useOrders();
  const { data: userOrganizationId, isLoading: isLoadingOrg } = useUserOrganization();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [expandedOrder, setExpandedOrder] = useState<string | null>(null);
  const [customerOrderDialogOpen, setCustomerOrderDialogOpen] = useState(false);
  const { session } = useAuth();
  const { toast } = useToast();

  // Check if user has any organization roles
  const { data: userRoles, isLoading: isLoadingRoles } = useQuery({
    queryKey: ["user-roles-check"],
    queryFn: async () => {
      if (!session) {
        return [];
      }
      const { data, error } = await supabase
        .from('user_roles')
        .select('role, organization_id')
        .eq('user_id', session.user.id);
      if (error) {
        console.error("Error fetching user roles:", error);
        throw error;
      }
      return data;
    },
    enabled: !!session
  });

  const { data: userRole } = useQuery({
    queryKey: ["user-role"],
    queryFn: async () => {
      if (!session) return null;
      const { data: role } = await supabase.rpc('get_user_role', {
        user_id: session.user.id
      });
      return role;
    },
    enabled: !!session
  });

  // Check if user has no roles/organizations
  const hasNoOrganization = !isLoadingRoles && userRoles !== undefined && userRoles.length === 0;

  const generateOrderSummaryPDF = async (order: any) => {
    try {
      // First, fetch the deal data by following the chain: order -> sale -> deal
      const { data: sale, error: saleError } = await supabase
        .from('sales')
        .select('deal_id')
        .eq('id', order.sale_id)
        .maybeSingle();

      if (saleError) {
        console.error('Error fetching sale:', saleError);
        toast({
          title: "Error",
          description: "Failed to fetch order details. Please try again.",
          variant: "destructive",
        });
        return;
      }

      if (!sale || !sale.deal_id) {
        toast({
          title: "Error",
          description: "No deal found for this order.",
          variant: "destructive",
        });
        return;
      }

      // Now fetch the full deal details
      const { data: deal, error: dealError } = await supabase
        .from('crm_deals')
        .select(`
          *,
          contact:profiles!crm_deals_contact_id_fkey(
            id,
            first_name,
            last_name,
            email,
            organization:organizations(name)
          ),
          organization:organizations(name),
          assigned_staff:crm_staff!crm_deals_assigned_to_staff_id_fkey(first_name, last_name),
          created_by_staff:crm_staff!crm_deals_created_by_staff_id_fkey(first_name, last_name),
          assigned_partner:crm_partners!crm_deals_assigned_to_partner_id_fkey(first_name, last_name),
          created_by_partner:crm_partners!crm_deals_created_by_partner_id_fkey(first_name, last_name)
        `)
        .eq('id', sale.deal_id)
        .maybeSingle();

      if (dealError) {
        console.error('Error fetching deal:', dealError);
        toast({
          title: "Error",
          description: "Failed to fetch deal details. Please try again.",
          variant: "destructive",
        });
        return;
      }

      if (!deal) {
        toast({
          title: "Error",
          description: "Deal details not found.",
          variant: "destructive",
        });
        return;
      }

      // Now generate the comprehensive PDF using the DealCalculator logic
      const doc = new jsPDF({ compress: true });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      
      // Helper function to format currency with thousand separators
      const formatCurrency = (amount: number): string => {
        return amount.toLocaleString('en-GB', { 
          minimumFractionDigits: 2, 
          maximumFractionDigits: 2 
        });
      };
      
      // Function to generate the PDF content
      const generatePDFContent = () => {
        const dealData = (deal.deal_data as any) || {};
        const fin = computeOrderFinancials(deal);
        const {
          orderType,
          paymentType,
          cameras,
          totalCreditsNeeded,
          pricePerImage,
          cameraPrice,
          cameraCost,
          accessoriesCost,
          dealAccessories,
          cameraModel,
          currentPlan,
          coverageYears,
          annualCredits,
          annualCreditsGross,
          annualCreditsNet,
          discountRows,
          subtotalGross,
          year1Total,
          finalTotal,
          contractValue,
          averageCostPerDay,
        } = fin;

        // Main Header - Check if deal has order reference
        doc.setFontSize(28);
        doc.setTextColor(0, 0, 0);
        const hasOrderReference = deal.order_reference;
        const documentTitle = hasOrderReference ? 'ORDER SUMMARY' : 'QUOTE';
        doc.text(documentTitle, pageWidth - (hasOrderReference ? 105 : 60), 25);
        
        // Two-column header layout
        doc.setFontSize(9);
        doc.setFont(undefined, 'normal');
        
        // Left Column - Company Details
        let leftY = 40;
        doc.setFont(undefined, 'bold');
        doc.text(COMPANY_NAME, 20, leftY);
        doc.setFont(undefined, 'normal');
        COMPANY_ADDRESS_LINES.forEach((line) => {
          doc.text(line, 20, leftY += 5);
        });
        
        // Right Column - Quote Details
        const rightX = pageWidth - 80;
        let rightY = 40;
        
        const currentDate = new Date().toLocaleDateString('en-GB', { 
          day: 'numeric', 
          month: 'short', 
          year: 'numeric' 
        });
        const expiryDate = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toLocaleDateString('en-GB', { 
          day: 'numeric', 
          month: 'short', 
          year: 'numeric' 
        });
        
        doc.text(`Date: ${currentDate}`, rightX, rightY);
        doc.text(`Expiry: ${expiryDate}`, rightX, rightY += 5);
        const quoteNumber = deal.quote_number || `QU-${new Date().getFullYear()}-PENDING`;
        doc.text(`Quote Number: ${quoteNumber}`, rightX, rightY += 5);
        doc.text(`Reference: ${deal.order_reference || ''}`, rightX, rightY += 5);
        
        // Customer Details Section - Show who the order is for
        let customerY = rightY + 10;
        doc.setFont(undefined, 'bold');
        doc.text('Order For:', 20, customerY);
        doc.setFont(undefined, 'normal');
        
        // Show contact name first
        if (deal.contact) {
          const contact = deal.contact;
          doc.text(`${contact.first_name || ''} ${contact.last_name || ''}`.trim(), 20, customerY += 5);
        }
        
        // Show organization from deal
        if (deal.organization?.name) {
          doc.text(deal.organization.name, 20, customerY += 5);
        }
        
        // Show contact email
        if (deal.contact?.email) {
          doc.text(deal.contact.email, 20, customerY += 5);
        }
        
        // Update rightY for subsequent content
        rightY = customerY;
        
        // Order Summary Section
        const summaryY = Math.max(leftY, rightY) + 15;
        doc.setFont(undefined, 'bold');
        doc.setFontSize(11);
        doc.text('Order Summary', 20, summaryY);
        
        doc.setFontSize(8);
        doc.setFont(undefined, 'normal');
        doc.text(`Plan: ${currentPlan.name} (${currentPlan.duration} year${currentPlan.duration > 1 ? 's' : ''})`, 20, summaryY + 8);
        doc.text(`Payment: ${paymentType === 'annual' ? 'Annual Contract' : 'Upfront Payment'}`, 20, summaryY + 13);
        doc.text(`Per-image cost: £${pricePerImage.toFixed(2)}`, rightX, summaryY + 8);
        doc.text(`Average daily cost/camera: £${averageCostPerDay.toFixed(2)}`, rightX, summaryY + 13);
        
        // Build comprehensive table data with line items and financial summary
        const tableData = [];
        
        // Add line items
        if (orderType === 'cameras_and_credits') {
          // Camera line item
          tableData.push([
            cameraModel,
            cameras.toFixed(2),
            `£${formatCurrency(cameraPrice)}`,
            `£${formatCurrency(cameraCost)}`
          ]);

          // Credits line item — skipped entirely for Trial (Pilot Pack) deals
          if (currentPlan.name !== 'Trial') {
            let creditDescription = '';
            if (paymentType === 'annual') {
              creditDescription = `${currentPlan.name} Plan ${annualCredits.toLocaleString()} Images and ${currentPlan.duration} Year On Air`;
            } else {
              creditDescription = `${currentPlan.name} Plan ${totalCreditsNeeded.toLocaleString()} Images and ${currentPlan.duration} Year${currentPlan.duration > 1 ? 's' : ''} On Air`;
            }

            const creditUnitPrice = paymentType === 'annual' ? annualCreditsGross : annualCreditsGross * coverageYears;
            const creditQuantity = cameras;

            tableData.push([
              creditDescription,
              creditQuantity.toFixed(2),
              `£${formatCurrency(creditUnitPrice / creditQuantity)}`,
              `£${formatCurrency(creditUnitPrice)}`
            ]);
          }
        } else {
          // Credits only
          let creditDescription = '';
          if (paymentType === 'annual') {
            creditDescription = `${currentPlan.name} Plan ${annualCredits.toLocaleString()} Images and ${currentPlan.duration} Year On Air (Annual)`;
          } else {
            creditDescription = `${currentPlan.name} Plan ${totalCreditsNeeded.toLocaleString()} Images and ${currentPlan.duration} Year${currentPlan.duration > 1 ? 's' : ''} On Air`;
          }
          const creditsGross = paymentType === 'annual' ? annualCreditsGross : annualCreditsGross * coverageYears;
          tableData.push([
            creditDescription,
            '1.00',
            `£${formatCurrency(creditsGross)}`,
            `£${formatCurrency(creditsGross)}`
          ]);
        }

        // Add accessory line items from deal_data
        dealAccessories.forEach((acc: any) => {
          if (acc && acc.quantity > 0) {
            tableData.push([
              acc.name || 'Accessory',
              Number(acc.quantity).toFixed(2),
              `£${formatCurrency(Number(acc.unit_price) || 0)}`,
              `£${formatCurrency((Number(acc.quantity) || 0) * (Number(acc.unit_price) || 0))}`
            ]);
          }
        });

        // Add financial summary rows to the same table
        tableData.push(['', '', 'Subtotal:', `£${formatCurrency(subtotalGross)}`]);
        discountRows.forEach(d => {
          tableData.push(['', '', `${d.label}:`, `-£${formatCurrency(d.amount)}`]);
        });
        if (paymentType === 'annual' && !fin.isTrial) {
          tableData.push(['', '', 'Year 1 Total:', `£${formatCurrency(year1Total)}`]);
          if (coverageYears > 1) {
            tableData.push(['', '', 'Annual Payment:', `£${formatCurrency(annualCreditsNet)}`]);
          }
          tableData.push(['', '', 'Contract Value:', `£${formatCurrency(contractValue)}`]);
        } else {
          tableData.push(['', '', 'Total:', `£${formatCurrency(finalTotal)}`]);
        }
        
        // Generate unified accounting table
        (doc as any).autoTable({
          startY: summaryY + 20,
          head: [['Description', 'Qty', 'Unit Price', 'Total']],
          body: tableData,
          styles: {
            fontSize: 8,
            cellPadding: 3,
            lineColor: [255, 255, 255],
            lineWidth: 0,
            halign: 'left',
          },
          headStyles: {
            fillColor: [240, 240, 240],
            textColor: [0, 0, 0],
            fontStyle: 'bold',
            lineColor: [255, 255, 255],
            lineWidth: 0,
            halign: 'left',
          },
          bodyStyles: {
            lineColor: [255, 255, 255],
            lineWidth: 0,
            halign: 'left',
          },
          columnStyles: {
            0: { cellWidth: 90, halign: 'left' },
            1: { cellWidth: 25, halign: 'left' },
            2: { cellWidth: 35, halign: 'left' },
            3: { cellWidth: 35, halign: 'left' }
          },
          didParseCell: function(data: any) {
            // Style financial summary rows
            const rowIndex = data.row.index;
            const isFinancialRow = rowIndex >= (orderType === 'cameras_and_credits' ? 3 : 2); // After line items + empty row
            const isEmptyRow = data.cell.text[0] === '' && data.column.index === 0 && rowIndex >= (orderType === 'cameras_and_credits' ? 2 : 1);
            const isTotalRow = data.cell.text[0] && (data.cell.text[0].includes('Total:') || data.cell.text[0].includes('Contract Value:'));
            
            if (isEmptyRow) {
              // Make empty spacing row invisible
              data.cell.styles.lineWidth = 0;
              data.cell.styles.fillColor = [255, 255, 255];
            } else if (isFinancialRow && !isEmptyRow) {
              // Style financial summary rows
              if (data.column.index >= 2) { // Only style the label and amount columns
                data.cell.styles.fontStyle = isTotalRow ? 'bold' : 'normal';
                data.cell.styles.fontSize = isTotalRow ? 9 : 8;
                data.cell.styles.lineWidth = 0;
                if (data.cell.text[0] && data.cell.text[0].includes('Discount')) {
                  data.cell.styles.textColor = [200, 0, 0]; // Red for discounts
                }
              } else {
                // Empty cells in financial summary rows
                data.cell.styles.lineWidth = 0;
                data.cell.text = [''];
              }
            } else {
              // Remove borders from all other cells
              data.cell.styles.lineWidth = 0;
            }
          },
          margin: { left: 20, right: 20 },
          tableLineColor: [255, 255, 255],
          tableLineWidth: 0,
          theme: 'plain'
        });
        
        // Add note below table
        let currentY = (doc as any).lastAutoTable.finalY + 5;
        doc.setFontSize(9);
        doc.setTextColor(100, 100, 100);
        doc.text('All amounts are ex VAT', 20, currentY);
        currentY += 8;
        
        // Terms Section
        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.text('Basic Terms', 20, currentY);
        currentY += 8;
        
        doc.setFont(undefined, 'normal');
        doc.setFontSize(8);
        
        const effectiveCameraQuantity = orderType === 'cameras_and_credits' ? cameras : 0;
        
        const terms = [
          `On Air Period – Refers to the contracted service duration of ${currentPlan.duration} year${currentPlan.duration > 1 ? 's' : ''} during which camera connectivity is maintained and images are delivered.`,
          '',
          'Additional Credits – May be purchased at any time at the per-image rate of the originally selected package. Minimum purchase thresholds apply for extending the On Air period.',
          '',
          'Included Features – Image credit pricing includes privacy filtering, AI-driven image search*, and optional real-time notifications.',
          '',
          paymentType === 'annual'
            ? `Payment Structure – Annual Contract: Year 1 payment of £${formatCurrency(year1Total)} includes camera${cameras > 1 ? 's' : ''} and first year credits. Subsequent years: £${formatCurrency(annualCreditsNet)} annually for ${currentPlan.duration - 1} year${currentPlan.duration - 1 > 1 ? 's' : ''}.`
            : 'Payment Options – Full Upfront Payment: Provides full access to all purchased credits for the entire coverage period.',
          '',
          'Image Retention – Images are stored on the Snapshot Live platform for 90 days. Customers may extend storage by ingesting images into their own system via API**.',
          '',
          '*see full terms and conditions for details',
          '**API integration cost POA'
        ];
        
        terms.forEach(term => {
          if (term === '') {
            currentY += 3;
          } else {
            const lines = doc.splitTextToSize(term, pageWidth - 40);
            doc.text(lines, 20, currentY);
            currentY += lines.length * 3.5 + 1;
          }
        });
        
        // Footer
        doc.setFontSize(8);
        doc.text(doc.splitTextToSize(COMPANY_PDF_FOOTER, pageWidth - 40), 20, doc.internal.pageSize.getHeight() - 13);
        
        // Save PDF
        const contact = deal.contact;
        const filePrefix = hasOrderReference ? 'Order_Summary' : 'Quote';
        const quoteRef = deal.quote_number || `TEMP-${Date.now().toString().slice(-4)}`;
        const fileName = `${filePrefix}_${quoteRef}_${contact ? `${contact.first_name}_${contact.last_name}` : deal.title?.replace(/\s+/g, '_') || order.id.slice(0, 8)}.pdf`;
        doc.save(fileName);
        
        toast({
          title: `${hasOrderReference ? 'Order Summary' : 'Quote'} Downloaded`,
          description: `Professional ${hasOrderReference ? 'order summary' : 'quotation'} saved as ${fileName}`,
        });
      };
      
      // Try to add logo, fallback to text if failed
      const logoImg = document.createElement('img');
      logoImg.onload = function() {
        try {
          // Downscale logo to keep PDF small (source is 4321x1239)
          const targetW = 480;
          const scale = targetW / logoImg.width;
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (!ctx) throw new Error('Canvas context not available');
          canvas.width = targetW;
          canvas.height = Math.round(logoImg.height * scale);
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(logoImg, 0, 0, canvas.width, canvas.height);
          const logoDataUrl = canvas.toDataURL('image/jpeg', 0.85);

          const logoWidth = 60;
          const logoHeight = (logoImg.height / logoImg.width) * logoWidth;
          doc.addImage(logoDataUrl, 'JPEG', 20, 15, logoWidth, logoHeight, undefined, 'FAST');
          
          generatePDFContent();
        } catch (error) {
          console.error('Error adding logo to PDF:', error);
          // Fallback to text if logo fails
          doc.setFontSize(24);
          doc.setFont(undefined, 'bold');
          doc.setTextColor(0, 100, 150);
          doc.text('SNAPSHOT LIVE', 20, 25);
          generatePDFContent();
        }
      };
      
      logoImg.onerror = function() {
        console.error('Failed to load logo, using text fallback');
        // Fallback to text if logo fails to load
        doc.setFontSize(24);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(0, 100, 150);  
        doc.text('SNAPSHOT LIVE', 20, 25);
        generatePDFContent();
      };
      
      logoImg.src = '/snapshot-logo.png';
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast({
        title: "Error",
        description: "Failed to generate PDF. Please try again.",
        variant: "destructive",
      });
    }
  };

  const filteredOrders = orders.filter(order => {
    const title = order.sale?.deal?.title ? String(order.sale.deal.title).toLowerCase() : "";
    const contactEmail = order.sale?.deal?.contact?.email ? String(order.sale.deal.contact.email).toLowerCase() : "";
    const tracking = order.tracking_number ? String(order.tracking_number).toLowerCase() : "";

    const term = (searchTerm || "").toLowerCase();
    const matchesSearch = term === "" ||
      title.includes(term) ||
      contactEmail.includes(term) ||
      tracking.includes(term);
    
    const matchesStatus = statusFilter === "all" || order.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  if (isLoading || isLoadingOrg) {
    return (
      <SidebarProvider>
        <div className="min-h-screen flex w-full bg-background">
          <AppSidebar />
          <SidebarInset>
            <div className="p-8">
              <div className="animate-pulse space-y-4">
                <div className="h-8 bg-muted rounded w-1/4"></div>
                <div className="h-32 bg-muted rounded"></div>
                <div className="h-32 bg-muted rounded"></div>
              </div>
            </div>
          </SidebarInset>
        </div>
      </SidebarProvider>
    );
  }

  return (
    <ErrorBoundary componentName="AdminOrders">
      <SidebarProvider>
        <div className="min-h-screen flex w-full bg-background">
          <AppSidebar />
          <SidebarInset>
            <FixedPageHeader {...PAGE_HEADERS.adminOrders} />
            <div className="px-4 md:px-8 py-6 md:py-8 space-y-8" style={{ paddingTop: "8.5rem" }}>
              
              {/* Organization Context */}
              {userOrganizationId && (
                <div className="bg-muted/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Package className="h-4 w-4" />
                    <span>Showing orders for organization: <span className="font-mono">{userOrganizationId}</span></span>
                  </div>
                </div>
              )}

              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Total Orders</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{orders.length}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Pending Payment</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-orange-600">
                      {orders.filter(o => o.status === 'pending' || o.status === 'waiting_for_payment').length}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">In Progress</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-orange-600">
                      {orders.filter(o => o.status === 'order_processing').length}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Delivered</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-green-600">
                      {orders.filter(o => o.status === 'delivered').length}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Customer Order Creation Section */}
              {!hasNoOrganization && !isLoadingRoles && (
                <div className="hidden mb-6 p-6 rounded-lg border border-teal-light" style={{ backgroundColor: "#F0EDE6" }}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold mb-2 text-left" style={{ color: "#321521" }}>Need Cameras or Images?</h3>
                      <p className="mb-4" style={{ color: "#321521" }}>
                        Create your own custom order proposal with cameras and image packages tailored to your needs.
                      </p>
                    </div>
                     <Button 
                        onClick={() => setCustomerOrderDialogOpen(true)} 
                        className="flex items-center gap-2 text-black hover:opacity-80 px-8 py-4 text-lg" 
                        style={{ backgroundColor: "#DFEF97" }}
                      >
                       <div className="bg-white rounded-full p-1.5">
                         <PlusCircle className="h-3 w-3 text-black stroke-2" />
                       </div>
                       Create Order
                     </Button>
                  </div>
                </div>
              )}

              {/* Filters */}
              <div className="flex flex-col md:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    placeholder="Search orders by title, customer, or tracking number..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-48">
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="waiting_for_payment">Waiting Payment</SelectItem>
                    <SelectItem value="order_processing">Processing</SelectItem>
                    <SelectItem value="delivered">Delivered</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Orders List */}
              <div className="space-y-4">
                {filteredOrders.map((order) => {
                  const progress = computeOrderProgress(order);
                  const stages = computeOrderStages(order);
                  const isExpanded = expandedOrder === order.id;

                  return (
                    <Card key={order.id} className="overflow-hidden">
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <div className="space-y-2">
                            <div className="flex items-center gap-3">
                              <h3 className="text-lg font-semibold">
                                {order.sale?.deal?.title || `Order #${(order.id || '').toString().slice(0, 8) || 'unknown'}`}
                              </h3>
                              <Badge className={getOrderStatusColor(order.status)}>
                                {(order.status ?? 'pending').replace(/_/g, ' ')}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground">
                              {order.sale?.deal?.contact && (
                                <div className="flex items-center gap-1">
                                  <User className="h-4 w-4" />
                                  <span>
                                    {order.sale.deal.contact.first_name} {order.sale.deal.contact.last_name}
                                  </span>
                                  <span className="text-xs">({order.sale.deal.contact.email})</span>
                                </div>
                              )}
                              <div className="flex items-center gap-1">
                                <Calendar className="h-4 w-4" />
                                <span>{order.created_at ? format(new Date(order.created_at), 'MMM dd, yyyy') : '—'}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Package className="h-4 w-4" />
                                <span>{order.total_cameras} cameras, {order.total_images} images</span>
                              </div>
                              {order.sale?.total_amount && (
                                <div className="flex items-center gap-1">
                                  <PoundSterling className="h-4 w-4" />
                                  <span>£{order.sale.total_amount}</span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => generateOrderSummaryPDF(order)}
                            >
                              <FileDown className="h-4 w-4 mr-2" />
                              Order Summary
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setExpandedOrder(isExpanded ? null : order.id)}
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              {isExpanded ? 'Hide' : 'Details'}
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                      
                      <CardContent>
                        {/* Progress Bar */}
                        <div className="space-y-2 mb-4">
                          <div className="flex justify-between text-sm">
                            <span className="font-medium">Order Progress</span>
                            <span className="text-muted-foreground">{Math.round(progress)}% Complete</span>
                          </div>
                          <Progress value={progress} className={`h-2 ${getProgressBarColor(order.status)}`} />
                        </div>

                        {/* Expanded Details */}
                        {isExpanded && (
                          <div className="space-y-6 pt-4 border-t">
                            {/* Timeline */}
                            <div>
                              <h4 className="text-sm font-semibold mb-4">Order Timeline</h4>
                              <div className="space-y-4">
                                {stages.map((stage, index) => {
                                  const Icon = stage.icon;
                                  return (
                                    <div key={index} className="flex items-center gap-3">
                                      <div className={`p-2 rounded-full ${
                                        stage.completed 
                                          ? 'bg-green-100 text-green-600' 
                                          : 'bg-gray-100 text-gray-400'
                                      }`}>
                                        <Icon className="h-4 w-4" />
                                      </div>
                                      <div className="flex-1">
                                        <div className="flex items-center justify-between">
                                          <span className={`text-sm font-medium ${
                                            stage.completed ? 'text-foreground' : 'text-muted-foreground'
                                          }`}>
                                            {stage.label}
                                          </span>
                                          {stage.date && (
                                            <span className="text-xs text-muted-foreground">
                                              {format(new Date(stage.date), 'MMM dd, HH:mm')}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>

                            {/* Additional Details */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              {/* Shipping Information */}
                              {(order.delivery_address_record || order.delivery_address || order.tracking_number) && (
                                <div>
                                  <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                                    <MapPin className="h-4 w-4" />
                                    Shipping Information
                                  </h4>
                                  <div className="space-y-2 text-sm">
                                    <DeliveryAddressBlock
                                      address={order.delivery_address_record}
                                      legacyAddress={order.delivery_address}
                                      compact
                                    />
                                    {order.tracking_number && (
                                      <div>
                                        <span className="font-medium">Tracking:</span>
                                        <span className="ml-2 font-mono">{order.tracking_number}</span>
                                      </div>
                                    )}
                                    {order.estimated_delivery_date && (
                                      <div>
                                        <span className="font-medium">Est. Delivery:</span>
                                        <span className="ml-2">
                                          {format(new Date(order.estimated_delivery_date), 'MMM dd, yyyy')}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Order Details */}
                              <div>
                                <h4 className="text-sm font-semibold mb-2">Order Details</h4>
                                <div className="space-y-2 text-sm">
                                  <div>
                                    <span className="font-medium">Order Type:</span>
                                    <span className="ml-2">{order.order_type?.replace('_', ' ') || 'Standard'}</span>
                                  </div>
                                  {order.allocated_device_ids?.length > 0 && (
                                    <div>
                                      <span className="font-medium">Allocated Devices:</span>
                                      <div className="mt-1 flex flex-wrap gap-1">
                                        {order.allocated_device_ids.map((deviceId: string) => (
                                          <Badge key={deviceId} variant="outline" className="text-xs">
                                            {deviceId}
                                          </Badge>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  {order.fulfillment_notes && (
                                    <div>
                                      <span className="font-medium">Notes:</span>
                                      <p className="text-muted-foreground mt-1">{order.fulfillment_notes}</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}

                {filteredOrders.length === 0 && (
                  <Card>
                    <CardContent className="text-center py-8">
                      <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">
                        {searchTerm || statusFilter !== "all" 
                          ? "No orders match your current filters." 
                          : "No orders found for your organization."}
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </SidebarInset>
        </div>

        {/* Customer Order Dialog */}
        <CustomerOrderDialog 
          open={customerOrderDialogOpen} 
          onOpenChange={setCustomerOrderDialogOpen} 
        />
      </SidebarProvider>
    </ErrorBoundary>
  );
}